/**
 * NewChunkModal — create-only flow.
 *
 * Replaces the prior ChunkEditModal for create. Editing now happens inline in
 * the right-pane ChunkPreview. The "Draft from URL" flow lives at the top of
 * this modal (was previously a separate page section).
 */
import { useState } from 'react';
import type { Sector } from '../../types';
import { ApiError } from '../../api/client';
import {
  type ChunkSource,
  useCreateChunk,
  useDraftFromText,
  useDraftFromUrl,
  type IntelChunk,
} from '../../hooks/useIntelChunks';
import {
  ChunkFormFields,
  type ChunkFormState,
  csvSplit,
} from './ChunkFormFields';
import styles from './NewChunkModal.module.css';

interface Props {
  sectors: Sector[];
  /** Initial sector + week the user was viewing — pre-fills the form. */
  defaultSectorId: number;
  defaultWeekOf: string;
  /** Future meeting Mondays — populates the meeting-week dropdown. */
  meetingDates: string[];
  /** Resolves a sector id to the Monday of its next scheduled agenda
   *  appearance. Called when the user changes sector so the meeting-week
   *  auto-updates to where the sector will next be discussed. */
  getDefaultWeekForSector: (sectorId: number) => string;
  onClose: () => void;
  /** Notify the parent so it can scroll the new chunk into view / select it. */
  onCreated?: (chunk: IntelChunk) => void;
}

function makeInitial(sectorId: number, weekOf: string): ChunkFormState {
  return {
    sector_id: sectorId,
    week_of: weekOf,
    event_date: '',
    quadrant: 'market_trends',
    bold_lead: '',
    body: '',
    relevance_to_ember: '',
    entities: '',
    tags: '',
    portfolio_companies: '',
    sources: [],
  };
}

function applyDraftToForm(
  form: ChunkFormState,
  draft: IntelChunk,
): ChunkFormState {
  return {
    ...form,
    sector_id: draft.sector_id,
    week_of: draft.week_of,
    event_date: draft.event_date ?? '',
    quadrant: draft.quadrant,
    bold_lead: draft.bold_lead,
    body: draft.body,
    relevance_to_ember: draft.relevance_to_ember ?? '',
    entities: draft.entities.join(', '),
    tags: draft.tags.join(', '),
    portfolio_companies: draft.portfolio_companies.join(', '),
    sources: draft.sources,
  };
}

export function NewChunkModal({
  sectors,
  defaultSectorId,
  defaultWeekOf,
  meetingDates,
  getDefaultWeekForSector,
  onClose,
  onCreated,
}: Props) {
  const [form, setForm] = useState<ChunkFormState>(() =>
    makeInitial(defaultSectorId, defaultWeekOf),
  );
  const [draftedChunk, setDraftedChunk] = useState<IntelChunk | null>(null);

  // Draft-from-URL UI state.
  const [urlInput, setUrlInput] = useState('');
  const [pasteMode, setPasteMode] = useState<null | {
    reason: string;
    message?: string;
    originalUrl: string;
  }>(null);
  const [pasteText, setPasteText] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  const createMut = useCreateChunk();
  const draftFromUrl = useDraftFromUrl();
  const draftFromText = useDraftFromText();
  const isPending = createMut.isPending || draftFromUrl.isPending || draftFromText.isPending;

  const set = (patch: Partial<ChunkFormState>) =>
    setForm((prev) => {
      // When the user switches sectors pre-draft, re-target the meeting week
      // to that sector's next agenda appearance. Skip once a URL-draft has
      // been created — its week is already persisted on the chunk row.
      if (
        patch.sector_id !== undefined &&
        patch.sector_id !== prev.sector_id &&
        !draftedChunk
      ) {
        return { ...prev, ...patch, week_of: getDefaultWeekForSector(patch.sector_id) };
      }
      return { ...prev, ...patch };
    });

  const handleDraftFromUrl = () => {
    if (!urlInput.trim()) return;
    setDraftError(null);
    draftFromUrl.mutate(
      { url: urlInput.trim(), sector_id: form.sector_id, week_of: form.week_of },
      {
        onSuccess: (chunk) => {
          // The draft endpoint creates a draft row server-side. Treat that
          // as "the new chunk" — no need to call create_chunk after editing;
          // edits will happen via the right-pane preview.
          setDraftedChunk(chunk);
          setForm((prev) => applyDraftToForm(prev, chunk));
          setUrlInput('');
          setPasteMode(null);
          setPasteText('');
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 422) {
            const detail = err.detail as
              | { error?: string; fallback?: string; message?: string }
              | undefined;
            if (detail?.fallback === 'paste_text') {
              setPasteMode({
                reason: detail.error || 'extraction_failed',
                message: detail.message,
                originalUrl: urlInput.trim(),
              });
              return;
            }
          }
          setDraftError(err.message);
        },
      },
    );
  };

  const handleDraftFromText = () => {
    if (!pasteText.trim()) return;
    setDraftError(null);
    draftFromText.mutate(
      {
        text: pasteText.trim(),
        sector_id: form.sector_id,
        week_of: form.week_of,
        source_url: pasteMode?.originalUrl || null,
        source_label: pasteMode?.originalUrl || null,
      },
      {
        onSuccess: (chunk) => {
          setDraftedChunk(chunk);
          setForm((prev) => applyDraftToForm(prev, chunk));
          setPasteMode(null);
          setPasteText('');
        },
        onError: (err) => setDraftError(err.message),
      },
    );
  };

  const handleSubmit = () => {
    if (!form.bold_lead.trim() || !form.body.trim()) {
      alert('Lead and body are required.');
      return;
    }
    // If the URL-draft path was used, the server already created the chunk
    // — close the modal and let the parent select it for further editing.
    if (draftedChunk) {
      onCreated?.(draftedChunk);
      onClose();
      return;
    }
    const sources: ChunkSource[] = form.sources.filter(
      (s) => s.label.trim() && s.url.trim(),
    );
    createMut.mutate(
      {
        sector_id: form.sector_id,
        week_of: form.week_of,
        event_date: form.event_date || null,
        quadrant: form.quadrant,
        bold_lead: form.bold_lead.trim(),
        body: form.body.trim(),
        relevance_to_ember: form.relevance_to_ember.trim() || null,
        entities: csvSplit(form.entities),
        tags: csvSplit(form.tags),
        portfolio_companies: csvSplit(form.portfolio_companies),
        sources,
      },
      {
        onSuccess: (created) => {
          onCreated?.(created);
          onClose();
        },
        onError: (e: Error) => alert(`Save failed: ${e.message}`),
      },
    );
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.title}>New chunk</div>
            <div className={styles.subtitle}>
              {sectors.find((s) => s.id === form.sector_id)?.name ?? 'Sector'} · {form.week_of}
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.body}>
          <section className={styles.draftSection}>
            <div className={styles.draftHeaderRow}>
              <h3 className={styles.sectionTitle}>Draft from URL</h3>
              {draftedChunk && (
                <span className={styles.draftedBadge}>Draft pre-filled · review below</span>
              )}
            </div>
            <p className={styles.sectionHelp}>
              Paste an article URL. Haiku extracts the readable text and pre-fills the form below
              for you to review and approve.
            </p>
            <div className={styles.urlRow}>
              <input
                type="url"
                className={styles.urlInput}
                placeholder="https://example.com/article"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                disabled={isPending}
              />
              <button
                className={styles.draftBtn}
                onClick={handleDraftFromUrl}
                disabled={!urlInput.trim() || isPending}
              >
                {draftFromUrl.isPending ? 'Drafting…' : 'Draft'}
              </button>
            </div>
            {pasteMode && (
              <div className={styles.pasteFallback}>
                <div className={styles.pasteReason}>
                  Couldn't extract text from that URL
                  {pasteMode.message ? <> — {pasteMode.message}</> : null}. Paste the
                  article text below to run the same pipeline.
                </div>
                <textarea
                  className={styles.pasteArea}
                  placeholder="Paste article text here (at least a few paragraphs)…"
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  rows={5}
                  disabled={isPending}
                />
                <div className={styles.pasteActions}>
                  <button
                    className={styles.cancelInlineBtn}
                    onClick={() => {
                      setPasteMode(null);
                      setPasteText('');
                    }}
                    disabled={isPending}
                  >
                    Cancel paste
                  </button>
                  <button
                    className={styles.draftBtn}
                    onClick={handleDraftFromText}
                    disabled={!pasteText.trim() || isPending}
                  >
                    {draftFromText.isPending ? 'Drafting…' : 'Draft from text'}
                  </button>
                </div>
              </div>
            )}
            {draftError && <div className={styles.errorBanner}>{draftError}</div>}
          </section>

          <hr className={styles.divider} />

          <ChunkFormFields
            state={form}
            sectors={sectors}
            meetingDates={meetingDates}
            onChange={set}
          />
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={isPending}
          >
            {createMut.isPending
              ? 'Saving…'
              : draftedChunk
                ? 'Done — keep editing in preview'
                : 'Create draft'}
          </button>
        </div>
      </div>
    </div>
  );
}
